/**
 * Interests — the sales desk's contracts.
 *
 * An interest is one enquiry: somebody filled in the form on wendhq.com
 * (`source: 'site'`), or a member of staff typed in a lead that arrived some
 * other way (`source: 'admin'`). Both land in one list on the admin panel so
 * that, in the owner's words, "all the sales tickets are at one place".
 *
 * A **leaf**: it imports `../common.js` and nothing else, and nothing imports
 * it back. An enquiry is not a tenant, a contact or a campaign, and the moment
 * this file reaches for one of those vocabularies is the moment a stranger's
 * enquiry starts looking like tenant data — which is the confusion migration
 * 0040 exists to rule out at the database.
 *
 * WHY THE PUBLIC REQUEST IS ITS OWN SCHEMA
 * ----------------------------------------------------------------------------
 * `publicInterestRequestSchema` is what an unauthenticated stranger may send,
 * and it is deliberately narrower than what staff may: no phone (the page does
 * not ask for one), no status, no source. It is `.strict()` so a body naming
 * `status: 'closed'` or `source: 'admin'` is refused with the key named rather
 * than silently stripped — a public write that quietly ignores fields is one
 * refactor away from a public write that quietly honours them.
 *
 * `website` is the honeypot. The page renders it off-screen and a person never
 * fills it in; a form-filling bot fills in everything. The API answers such a
 * submission exactly as it answers a real one and stores nothing — see
 * `interests-intake.service.ts`.
 */
import { z } from 'zod';

import {
  emailSchema,
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  uuidSchema,
} from '../common.js';

/* -------------------------------------------------------------------------- */
/* Vocabularies                                                                */
/* -------------------------------------------------------------------------- */

/** `interests_status_known` in 0040 names exactly these. */
export const INTEREST_STATUS_VALUES = ['new', 'in_progress', 'closed'] as const;
export const interestStatusSchema = z.enum(INTEREST_STATUS_VALUES);
export type InterestStatus = z.infer<typeof interestStatusSchema>;

/** `interests_source_known` in 0040 names exactly these. */
export const INTEREST_SOURCE_VALUES = ['site', 'admin'] as const;
export const interestSourceSchema = z.enum(INTEREST_SOURCE_VALUES);
export type InterestSource = z.infer<typeof interestSourceSchema>;

export const INTEREST_NOTE_KIND_VALUES = ['note', 'status'] as const;
export const interestNoteKindSchema = z.enum(INTEREST_NOTE_KIND_VALUES);
export type InterestNoteKind = z.infer<typeof interestNoteKindSchema>;

/**
 * What the list filters by. `open` is `new` + `in_progress` — the desk's
 * default view, because a closed enquiry is history and not work.
 */
export const INTEREST_FILTER_VALUES = ['open', 'new', 'in_progress', 'closed', 'all'] as const;
export const interestFilterSchema = z.enum(INTEREST_FILTER_VALUES);
export type InterestFilter = z.infer<typeof interestFilterSchema>;

/* -------------------------------------------------------------------------- */
/* Field limits — the same numbers 0040's CHECKs use                           */
/* -------------------------------------------------------------------------- */

export const MAX_INTEREST_NAME_LENGTH = 200;
export const MAX_INTEREST_ORGANISATION_LENGTH = 200;
export const MAX_INTEREST_PHONE_LENGTH = 40;
export const MAX_INTEREST_MESSAGE_LENGTH = 5000;
export const MAX_INTEREST_NOTE_LENGTH = 5000;

/**
 * Characters no field here may hold: the C0 controls and DEL (a NUL cannot be
 * stored in Postgres `text` at all, and would reach the database as a 500
 * rather than a 400), and the bidirectional overrides and isolates, which let a
 * name in the admin list render as something other than what was stored.
 * `allowLineBreaks` keeps tab, CR and LF for the free-text fields.
 *
 * Checked by code point rather than a regex literal so the set is readable as
 * numbers and survives any tool that rewrites escape sequences.
 */
function hasForbiddenCharacters(value: string, allowLineBreaks: boolean): boolean {
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (allowLineBreaks && (code === 0x09 || code === 0x0a || code === 0x0d)) continue;
    if (code <= 0x1f || code === 0x7f) return true;
    if ((code >= 0x202a && code <= 0x202e) || (code >= 0x2066 && code <= 0x2069)) return true;
  }
  return false;
}

/** A trimmed single-line field: no control characters, no direction overrides. */
const lineField = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .refine((value) => !hasForbiddenCharacters(value, false), 'Contains characters that cannot be stored.');

/** A trimmed multi-line field: line breaks and tabs allowed, other controls not. */
const textField = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .refine((value) => !hasForbiddenCharacters(value, true), 'Contains characters that cannot be stored.');

/*
 * The messages are neutral on purpose: the same fields serve a visitor on the
 * public page and a staff member typing in somebody else's lead, and "Tell us
 * who you are" is wrong in the second voice. The public page writes its own
 * sentences (`apps/site/src/lib/interest.ts`).
 */
const nameField = lineField(MAX_INTEREST_NAME_LENGTH).refine((value) => value !== '', 'A name is required.');
const organisationField = lineField(MAX_INTEREST_ORGANISATION_LENGTH).refine(
  (value) => value !== '',
  'A company is required.',
);
const messageField = textField(MAX_INTEREST_MESSAGE_LENGTH);

/* -------------------------------------------------------------------------- */
/* The public form                                                             */
/* -------------------------------------------------------------------------- */

export const publicInterestRequestSchema = z
  .object({
    name: nameField,
    email: emailSchema,
    organisation: organisationField,
    message: messageField.default(''),
    /** The honeypot. Empty from a person; anything else is a bot. */
    website: z.string().max(500).optional(),
  })
  .strict();
export type PublicInterestRequest = z.infer<typeof publicInterestRequestSchema>;

/**
 * The whole answer, and the same answer whether the row was stored or the
 * honeypot swallowed it. It carries no id: a stranger has no later use for one,
 * and an id would be a way to tell a stored submission from a dropped one.
 */
export const publicInterestResponseSchema = z.object({ received: z.literal(true) });
export type PublicInterestResponse = z.infer<typeof publicInterestResponseSchema>;

/* -------------------------------------------------------------------------- */
/* The desk                                                                    */
/* -------------------------------------------------------------------------- */

export const interestSummarySchema = z.object({
  id: uuidSchema,
  source: interestSourceSchema,
  status: interestStatusSchema,
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  organisation: z.string(),
  /** First line or so of the message, for the list. The detail has all of it. */
  messageExcerpt: z.string(),
  noteCount: z.number().int().min(0),
  /** The newest timeline entry, or the creation time when there is none. */
  lastActivityAt: isoDateTimeSchema,
  statusChangedAt: isoDateTimeSchema,
  createdAt: isoDateTimeSchema,
});
export type InterestSummary = z.infer<typeof interestSummarySchema>;

export const interestNoteSchema = z.object({
  id: uuidSchema,
  kind: interestNoteKindSchema,
  /** A note's text, or the optional reason given with a status change. */
  body: z.string().nullable(),
  fromStatus: interestStatusSchema.nullable(),
  toStatus: interestStatusSchema.nullable(),
  adminEmail: z.string(),
  createdAt: isoDateTimeSchema,
});
export type InterestNote = z.infer<typeof interestNoteSchema>;

export const interestDetailSchema = interestSummarySchema.omit({ messageExcerpt: true }).extend({
  message: z.string(),
  /** Who typed it in — `null` for a form submission. */
  createdByEmail: z.string().nullable(),
  /** Oldest first: a timeline reads down. */
  notes: z.array(interestNoteSchema),
});
export type InterestDetail = z.infer<typeof interestDetailSchema>;

export const interestParamsSchema = z.object({ interestId: uuidSchema });
export type InterestParams = z.infer<typeof interestParamsSchema>;

export const interestListQuerySchema = paginationQuerySchema.extend({
  status: interestFilterSchema.default('open'),
  /** Name, email, organisation or phone, case-insensitive. */
  search: lineField(200).optional(),
});
export type InterestListQuery = z.infer<typeof interestListQuerySchema>;

export const interestCountsSchema = z.object({
  new: z.number().int().min(0),
  in_progress: z.number().int().min(0),
  closed: z.number().int().min(0),
});
export type InterestCounts = z.infer<typeof interestCountsSchema>;

export const interestListResponseSchema = paginated(interestSummarySchema).extend({
  /** Across every interest, not just this page or this filter — the tab badges. */
  counts: interestCountsSchema,
});
export type InterestListResponse = z.infer<typeof interestListResponseSchema>;

/** Optional text: absent, empty and whitespace all mean "not given". */
const optionalText = (max: number, multiline: boolean) =>
  (multiline ? textField(max) : lineField(max))
    .optional()
    .transform((value) => (value === undefined || value === '' ? undefined : value));

/**
 * A lead staff typed in. Email **or** phone — somebody who rang has a number
 * and may not have given an address, and 0040's `interests_reachable` holds
 * the same line.
 */
export const createInterestRequestSchema = z
  .object({
    name: nameField,
    organisation: organisationField,
    /* Blank — absent, empty or only spaces — means "not given". */
    email: z.preprocess(
      (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
      z
        .string()
        .trim()
        .toLowerCase()
        .email('That does not look like an email address.')
        .max(254)
        .optional(),
    ),
    phone: optionalText(MAX_INTEREST_PHONE_LENGTH, false),
    message: messageField.default(''),
    status: interestStatusSchema.default('new'),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.email === undefined && value.phone === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: 'Give an email or a phone number, so somebody can follow up.',
      });
    }
  });
/** What the panel sends. */
export type CreateInterestRequest = z.input<typeof createInterestRequestSchema>;
/** What the API holds after validation: blanks are gone, defaults are in. */
export type CreateInterestInput = z.output<typeof createInterestRequestSchema>;

export const updateInterestStatusRequestSchema = z
  .object({
    status: interestStatusSchema,
    /** Why, in a line. Becomes part of the timeline entry. */
    note: optionalText(MAX_INTEREST_NOTE_LENGTH, true),
  })
  .strict();
export type UpdateInterestStatusRequest = z.input<typeof updateInterestStatusRequestSchema>;
export type UpdateInterestStatusInput = z.output<typeof updateInterestStatusRequestSchema>;

export const addInterestNoteRequestSchema = z
  .object({
    body: textField(MAX_INTEREST_NOTE_LENGTH).refine((value) => value !== '', 'Write something first.'),
  })
  .strict();
export type AddInterestNoteRequest = z.infer<typeof addInterestNoteRequestSchema>;
